import React, {useEffect, useState} from "react";
import { useDispatch, useSelector } from 'react-redux';
import ReactModal from "react-modal";
import { setShowModal, setTyping, setUserSettings } from "../store/playerUiSlice";
import { useGameL10n } from "./hooks/useGameL10n";
import { useGameDefinition } from "./hooks/useGameDefinition";
import { useSiteL10n } from "../../hooks/useSiteL10n";
import { usePlugin } from "./hooks/usePlugin";
import useProfile from "../../hooks/useProfile";
import Axios from "axios";
import { deepUpdate } from "../store/updateValues";
import { useAuthOptions } from "../../hooks/useAuthOptions";
import Button from "../../components/basic/Button";
import { useDoActionList } from "./hooks/useDoActionList";
import { usePlayerN } from "./hooks/usePlayerN";
import store from "../../store";
import { useIsHost } from "./hooks/useIsHost";
import { Z_INDEX } from "./functions/common";
import { ToggleSwitch } from "./AutomationModal";

// Move to another file, use this to set the default falues in playerUiSlice
export const uiSettings = {
  "backgroundUrl": {
    "id": "backgroundUrl",
    "label": "backgroundUrl",
    "type": "string",
    "default": "",
    "supporterLevel": 5
  },
  "zoomPercent": {
    "id": "zoomPercent",
    "label": "zoomPercent",
    "type": "integer",
    "default": 100
  },
  "touchMode": {
    "id": "touchMode",
    "label": "touchMode",
    "type": "boolean",
    "default": false
  },
  "rendererEngine": {
    "id": "rendererEngine",
    "label": "Render Mode",
    "type": "option",
    "segmented": true,
    "default": "dnc3d",
    "options": [
      { "id": "default", "label": "2D" },
      { "id": "dnc3d",   "label": "3D" }
    ]
  },
  "tableAngle": {
    "id": "tableAngle",
    "label": "Table Angle",
    "type": "slider",
    "min": 0,
    "max": 45,
    "step": 1,
    "default": 25,
    "live": true,
    "visibleWhen": { "rendererEngine": "dnc3d" },
    "note": "On smaller screens, applying a tilt to the table may make card images appear slightly blurry."
  },
  "alwaysShowPileSizes": {
    "id": "alwaysShowPileSizes",
    "label": "Always Show Pile Sizes",
    "type": "boolean",
    "default": false,
    "visibleWhen": { "rendererEngine": "dnc3d" }
  },
  "alwaysShowGroupButtons": {
    "id": "alwaysShowGroupButtons",
    "label": "Always Show Group Buttons",
    "type": "boolean",
    "default": false,
    "visibleWhen": { "rendererEngine": "dnc3d" }
  }
}

export const SettingsModal = React.memo(({}) => {
    const dispatch = useDispatch();

    return(
      <ReactModal
        closeTimeoutMS={200}
        isOpen={true}
        onRequestClose={() => {
          dispatch(setShowModal(null));
          dispatch(setTyping(false));
        }}
        contentLabel={"Load quest"}
        overlayClassName="fixed inset-0 bg-black-50"
        className="insert-auto bg-gray-800 border border-gray-600 max-h-lg mx-auto mt-12 rounded-lg outline-none"
        style={{
          overlay: {
            zIndex: Z_INDEX.Modal
          },
          content: {
            width: "800px",
            maxHeight: "95dvh",
            overflowY: "auto",
          }
        }}>
        <div style={{padding: "20px 24px 8px 24px", borderBottom: "1px solid #374151"}}>
          <h1 style={{margin: 0, fontSize: "1.25rem", fontWeight: 600, color: "white", letterSpacing: "-0.01em"}}>
            Settings
          </h1>
          <p style={{margin: "4px 0 0 0", fontSize: "0.8rem", color: "#9ca3af"}}>
            Adjust UI, player, and game preferences
          </p>
        </div>
        <div style={{padding: "12px 24px 20px 24px"}}>
          <ModalContent/>
        </div>
      </ReactModal>  
    )
})

const ModalContent = () => {
  const dispatch = useDispatch();
  const doActionList = useDoActionList();
  const user = useProfile();
  const isHost = useIsHost();
  const authOptions = useAuthOptions();
  const plugin = usePlugin();
  const gameDef = useGameDefinition();
  const stateBackgroundUrl = useSelector(state => state?.playerUi?.userSettings?.backgroundUrl);
  const databasePlayerKeyVals = user?.plugin_settings?.[plugin?.id]?.player;
  const databaseGameKeyVals = user?.plugin_settings?.[plugin?.id]?.game;
  const databaseUiKeyVals = user?.plugin_settings?.[plugin?.id]?.ui;
  const gameDefPlayerProperties = gameDef?.playerProperties;
  const gameDefGameProperties = gameDef?.gameProperties;
  const gameDefPreferences = gameDef?.preferences;
  const playerN = usePlayerN();
  const siteL10n = useSiteL10n();
  const gameL10n = useGameL10n();
  const [currentUiKeyVals, setCurrentUiKeyVals] = useState({});
  const [currentPlayerKeyVals, setCurrentPlayerKeyVals] = useState({});
  const [currentGameKeyVals, setCurrentGameKeyVals] = useState({});
  const [onRenderPlayerKeyVals, setOnRenderPlayerKeyVals] = useState({});
  const [onRenderGameKeyVals, setOnRenderGameKeyVals] = useState({});
  const [defaultUiKeyVals, setDefaultUiKeyVals] = useState({});
  const [defaultPlayerKeyVals, setDefaultPlayerKeyVals] = useState({});
  const [defaultGameKeyVals, setDefaultGameKeyVals] = useState({});

  // Filter out keys in the game definition if their value (which is an object) does not have a showInSettings = true property
  const gameDefPlayerSettings = (gameDefPreferences?.player ?? []).reduce((obj, key) => {
    obj[key] = gameDefPlayerProperties[key];
    obj[key].id = key;
    return obj;
  }, {});

  const gameDefGameSettings = (gameDefPreferences?.game ?? []).reduce((obj, key) => {
    obj[key] = gameDefGameProperties[key];
    obj[key].id = key;
    return obj;
  }, {});

  useEffect(() => {
    dispatch(setTyping(true));
    const state = store.getState();
    // `|| {}` is load-bearing: a user who has not taken a seat has playerN
    // null, so there is no playerData entry to read their current values from.
    // Without it the reduce below throws, and with no error boundary in the app
    // that unmounts the whole table - a white page for anyone opening Settings
    // from a spectator seat (the one place they'd go to switch back to 2D).
    const statePlayerKeyVals = state?.gameUi?.game?.playerData?.[playerN] || {};
    const stateGameKeyVals = state?.gameUi?.game;
    const stateUiKeyVals = state?.playerUi?.userSettings;

    // Filter out to just the keys that should be displayed in the settings modal
    const settableStatePlayerKeyVals = Object.keys(gameDefPlayerSettings).reduce((obj, key) => {
      obj[key] = statePlayerKeyVals[key];
      return obj;
    }, {});
    
    const settableStateGameKeyVals = Object.keys(gameDefGameSettings).reduce((obj, key) => {
      obj[key] = stateGameKeyVals[key];
      return obj;
    }
    , {});

    const settableStateUiKeyVals = Object.keys(uiSettings).reduce((obj, key) => {
      obj[key] = stateUiKeyVals[key];
      return obj;
    }
    , {});

    setOnRenderPlayerKeyVals(settableStatePlayerKeyVals);
    setOnRenderGameKeyVals(settableStateGameKeyVals);
    // No need to set onRenderUiSettings because we always apply it even if it's unchanged

    setCurrentPlayerKeyVals(settableStatePlayerKeyVals);
    setCurrentGameKeyVals(settableStateGameKeyVals);
    setCurrentUiKeyVals(settableStateUiKeyVals);

    const settableDefaultPlayerKeyVals = Object.keys(gameDefPlayerSettings).reduce((obj, key) => {
      obj[key] = gameDefPlayerSettings[key].default;
      if (databasePlayerKeyVals && databasePlayerKeyVals[key] !== undefined) {
        obj[key] = databasePlayerKeyVals[key];
      }
      return obj;
    }
    , {});

    const settableDefaultGameKeyVals = Object.keys(gameDefGameSettings).reduce((obj, key) => {
      obj[key] = gameDefGameSettings[key].default;
      if (databaseGameKeyVals && databaseGameKeyVals[key] !== undefined) {
        obj[key] = databaseGameKeyVals[key];
      }
      return obj;
    }
    , {});

    const settableDefaultUiKeyVals = Object.keys(uiSettings).reduce((obj, key) => {
      obj[key] = uiSettings[key].default;
      if (databaseUiKeyVals && databaseUiKeyVals[key] !== undefined) {
        obj[key] = databaseUiKeyVals[key];
      }
      return obj;
    }
    , {});

    setDefaultPlayerKeyVals(settableDefaultPlayerKeyVals);
    setDefaultGameKeyVals(settableDefaultGameKeyVals);
    setDefaultUiKeyVals(settableDefaultUiKeyVals);
    
  }, [playerN, stateBackgroundUrl]);

  const handleSave = async () => {
    var url = currentUiKeyVals["background_url"];
    if (url && !(url.endsWith(".png") || url.endsWith(".jpg") || url.endsWith(".jpeg"))) {
      alert(siteL10n("altArtFormatError"))
      return;
    }
    if (url === "") {
      url = null;
    }

    // Update the database
    const newDatabasePluginSettings = {
      [plugin.id]: {
        player: defaultPlayerKeyVals,
        game: defaultGameKeyVals,
        ui: defaultUiKeyVals
      }
    };
    
    // If any settings are being updated, update the database
    const res = await Axios.post("/be/api/v1/profile/update_plugin_user_settings", newDatabasePluginSettings, authOptions);

    const pluginSettings = user.plugin_settings;
    deepUpdate(pluginSettings, newDatabasePluginSettings);
    const newProfileData = {
      user_profile: {
        ...user,
        plugin_settings: pluginSettings
      }}

    user.setData(newProfileData);
    if (res.status !== 200) {
      alert(siteL10n("settingUpdateError")); 
    }
    //user.doFetchHash(Date.now());
    
    // Update the game
    const actionList = [];
    for (const key of Object.keys(currentPlayerKeyVals)) {
      const label = gameDefPlayerProperties[key].label;
      const value = currentPlayerKeyVals[key];
      if (value !== onRenderPlayerKeyVals[key]) {
        actionList.push(["SET", `/playerData/$PLAYER_N/${key}`, value]);
        actionList.push(["LOG", `{{$ALIAS_N}} set their ${label} to ${value}.`]);
      }
    }
    for (const key of Object.keys(currentGameKeyVals)) {
      const label = gameDefGameProperties[key].label;
      const value = currentGameKeyVals[key];
      if (value !== onRenderGameKeyVals[key]) {
        actionList.push(["SET", `/${key}`, value]);
        actionList.push(["LOG", `{{$ALIAS_N}} set the ${label} to ${value}.`]);
      }
    }

    if (actionList.length > 0) {
      doActionList(actionList, `Updated settings for user ${user.alias}`);
    }

    // Update the UI settings
    dispatch(setUserSettings(currentUiKeyVals));

    // Close the modal
    dispatch(setTyping(false));
    dispatch(setShowModal(null));
  }

  
  const description = (label) => {
    return <div className="text-sm text-gray-300 mb-1">{siteL10n(label)}</div>
  }

  return(
    <div className="text-white">
      <h2 className="mb-1">{siteL10n("uiPreferences")}</h2>
      {description("uiPreferencesDescription")}
      <SettingModalTable settings={uiSettings} currentKeyVals={currentUiKeyVals} defaultKeyVals={defaultUiKeyVals} setCurrentFunction={setCurrentUiKeyVals} setDefaultFunction={setDefaultUiKeyVals} l10n={siteL10n}/>
      <h2 className="mb-1">{siteL10n("playerPreferences")}</h2>
      {description("playerPreferencesDescription")}
      <SettingModalTable settings={gameDefPlayerSettings} currentKeyVals={currentPlayerKeyVals} defaultKeyVals={defaultPlayerKeyVals} setCurrentFunction={setCurrentPlayerKeyVals} setDefaultFunction={setDefaultPlayerKeyVals} l10n={gameL10n}/>
      {isHost &&
        <div>
          <h2 className="mb-1">{siteL10n("gamePreferences")}</h2>
          {description("gamePreferencesDescription")}
          <SettingModalTable settings={gameDefGameSettings} currentKeyVals={currentGameKeyVals} defaultKeyVals={defaultGameKeyVals} setCurrentFunction={setCurrentGameKeyVals} setDefaultFunction={setDefaultGameKeyVals} l10n={gameL10n}/>
          <Button isPrimary className="my-2" onClick={() => dispatch(setShowModal("automation"))}>
            Automation Preferences
          </Button>
        </div>
      }
      <div className="flex items-center justify-between">
        <Button isSubmit isPrimary className="my-2 w-64" onClick={handleSave}>
          Update
        </Button>
      </div>
    </div>
  );
}

const SettingModalTable = React.memo(({settings, currentKeyVals, defaultKeyVals, setCurrentFunction, setDefaultFunction, l10n}) => {
  const baseClassName = "m-2 px-4 py-2 w-1/3";
  return (
    <table className="w-full text-sm mb-2">
      <tbody>
        <tr className="bg-gray-800 text-white">
          <th className={baseClassName}></th>
          <th className={baseClassName}>Current</th>
          <th className={baseClassName}>Default</th>
        </tr>
        {Object.keys(settings).reduce((rows, propertyId, index) => {
          const settingObj = settings[propertyId];
          if (settingObj.visibleWhen) {
            const visible = Object.entries(settingObj.visibleWhen).every(([k, v]) => currentKeyVals[k] === v);
            if (!visible) return rows;
          }
          const bgColour = rows.length % 2 === 0 ? "bg-gray-500" : "bg-gray-600";
          const className = baseClassName + " " + bgColour;
          rows.push(
            <tr key={propertyId}>
              <td className={className}>
                {l10n(settingObj.label)}
              </td>
              <td className={className}>
                <SettingsModalFormElement val={currentKeyVals[settingObj.id]} settingObj={settingObj} setFunction={setCurrentFunction} l10n={l10n}/>
              </td>
              <td className={className} style={{opacity: "70%"}}>
                <SettingsModalFormElement val={defaultKeyVals[settingObj.id]} settingObj={settingObj} setFunction={setDefaultFunction} l10n={l10n}/>
              </td>
            </tr>
          );
          return rows;
        }, [])}
      </tbody>
    </table>
  )
})

const SettingsModalFormElement = ({val, settingObj, setFunction, l10n}) => {
  const dispatch = useDispatch();
  const user = useProfile();
  const userSettings = useSelector(state => state?.playerUi?.userSettings);
  switch (settingObj.type) {
    case 'integer':
      return (
        <input
          type="number"
          className="p-1 w-16 text-black"
          value={val ?? ""}
          onChange={(e) => {
            const newValue = parseInt(e.target.value);
            setFunction(prevSettings => ({...prevSettings, [settingObj.id]: newValue}))
          }}
        />
      );
    case 'string':
      return (
        (settingObj.supporterLevel > user?.supporter_level && user.admin === false) ?
          <button 
            className="flex items-center justify-center rounded text-white bg-gray-800 hover:bg-gray-700 p-1 px-4"
            onClick={() => {dispatch(setShowModal("patreon"))}}
            >
            <span>Unlock</span>
            <img className="ml-2" style={{height: "20px"}} src="https://upload.wikimedia.org/wikipedia/commons/9/94/Patreon_logo.svg" alt="Patreon logo"/>
          </button>
          :
          <input
            disabled={settingObj.supporterLevel > user?.supporter_level && user.admin === false}
            type="text"
            className="p-1 w-48 text-black"
            value={val ?? ""}
            onChange={(e) => {
              const newValue = e.target.value;
              setFunction(prevSettings => ({ ...prevSettings, [settingObj.id]: newValue }));
            }}
          />
      );
    case 'option':
      // Segmented button group (opt-in via `segmented: true`); all other option
      // settings keep the standard dropdown.
      if (settingObj.segmented) {
        return (
          <div className="inline-flex gap-1 rounded-md bg-gray-800 p-1">
            {settingObj.options.map(option => {
              const selected = val === option.id;
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    setFunction(prevSettings => ({...prevSettings, [settingObj.id]: option.id}))
                  }
                  className={
                    "rounded px-3 py-1 text-sm transition-colors " +
                    (selected
                      ? "bg-blue-800 text-white shadow"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white")
                  }
                >
                  {l10n(option.label)}
                </button>
              );
            })}
          </div>
        );
      }
      return (
        <select
          className="p-2 w-48 text-black"
          value={val}
          onChange={(e) => {
            const newValue = e.target.value;
            setFunction(prevSettings => ({...prevSettings, [settingObj.id]: newValue}));
          }}
        >
          {settingObj.options.map(option => (
            <option key={option.id} value={option.id}>{l10n(option.label)}</option>
          ))}
        </select>
      );
    case 'slider': {
      const sliderVal = val ?? settingObj.default ?? 0;
      return (
        <div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={settingObj.min ?? 0}
              max={settingObj.max ?? 100}
              step={settingObj.step ?? 1}
              value={sliderVal}
              className="w-24 accent-blue-500"
              onChange={(e) => {
                const newValue = parseInt(e.target.value);
                setFunction(prevSettings => ({...prevSettings, [settingObj.id]: newValue}));
                if (settingObj.live) {
                  dispatch(setUserSettings({...userSettings, [settingObj.id]: newValue}));
                }
              }}
            />
            <span className="text-xs w-8 text-right tabular-nums">{sliderVal}°</span>
          </div>
          {settingObj.note && (
            <div className="text-xs text-gray-300 mt-1">{l10n(settingObj.note)}</div>
          )}
        </div>
      );
    }
    case 'boolean':
      return (
        <ToggleSwitch
          checked={val}
          onChange={(newValue) =>
            setFunction(prevSettings => ({...prevSettings, [settingObj.id]: newValue}))
          }
        />
      );
    default:
      return null;
  }
};